import React, { useContext, useState } from 'react'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Flex,
  Card,
  Form,
  Input,
  message,
} from 'antd'
import { useNavigate } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { isAxiosError } from 'axios'
import { AuthContext } from '@/contexts/AuthContext'
import { updateUser } from '@/api/users/updateUser'

const { Title } = Typography
const { Content } = Layout

interface ProfileFormValues {
  name: string
  email: string
  newPassword?: string
  confirmPassword?: string
}

export const ProfileEditor: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!
  const user = auth.user!
  const [form] = Form.useForm<ProfileFormValues>()

  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      const { name, email, newPassword } = values

      setSaving(true)
      await updateUser(String(user.id), {
        name,
        email,
        ...(newPassword ? { password: newPassword } : {}),
      })

      form.setFieldsValue({
        newPassword: '',
        confirmPassword: '',
      })
      message.success(
        newPassword ? 'Profile and password updated' : 'Profile updated',
      )
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 422) {
        const apiErrors = err.response.data?.errors as
          | Record<string, string[]>
          | undefined
        const mappedFields = Object.entries(apiErrors ?? {})
          .map(([field, errors]) => {
            const [firstError] = errors
            if (!firstError) return null
            const formField = field === 'password' ? 'newPassword' : field
            return {
              name: formField as keyof ProfileFormValues,
              errors: [firstError],
            }
          })
          .filter(
            (
              entry,
            ): entry is { name: keyof ProfileFormValues; errors: string[] } =>
              Boolean(entry),
          )

        if (mappedFields.length > 0) {
          form.setFields(mappedFields)
          message.error('Please review the highlighted fields')
        } else {
          message.error('Failed to update profile')
        }
      } else {
        message.error('Failed to update profile')
      }
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb items={[{ title: 'Profile' }]} />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          />
          <Title level={3} style={{ margin: 0 }}>
            Edit Profile
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                name: user.name,
                email: user.email,
              }}
            >
              <Card title="Profile Info" style={{ maxWidth: 700 }}>
                <Form.Item label="Name">
                  <Form.Item
                    name="name"
                    noStyle
                    rules={[
                      { required: true, message: 'Please enter your name' },
                    ]}
                  >
                    <Input placeholder="Enter name" />
                  </Form.Item>
                </Form.Item>
                <Form.Item label="Email">
                  <Form.Item
                    name="email"
                    noStyle
                    rules={[
                      { required: true, message: 'Please enter your email' },
                      { type: 'email', message: 'Please enter a valid email' },
                    ]}
                  >
                    <Input placeholder="Enter email" />
                  </Form.Item>
                </Form.Item>
              </Card>

              <Card
                title="Change Password"
                style={{ maxWidth: 700, marginTop: 16 }}
              >
                <Form.Item
                  name="newPassword"
                  label="New Password"
                  rules={[
                    () => ({
                      validator(_, value?: string) {
                        if (!value) return Promise.resolve()
                        if (value.length < 8 || value.length > 255) {
                          return Promise.reject(
                            new Error(
                              'Password must be between 8 and 255 characters',
                            ),
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Leave blank to keep current password"
                    autoComplete="new-password"
                  />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="Confirm New Password"
                  validateTrigger="onSubmit"
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value?: string) {
                        const newPassword = getFieldValue('newPassword')
                        if (!newPassword && !value) return Promise.resolve()
                        if (newPassword && !value) {
                          return Promise.reject(
                            new Error('Please confirm your new password'),
                          )
                        }
                        if (value !== newPassword) {
                          return Promise.reject(
                            new Error(
                              'Confirmation does not match the new password',
                            ),
                          )
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    placeholder="Re-enter your new password"
                    autoComplete="new-password"
                  />
                </Form.Item>
              </Card>
            </Form>
            <Flex justify="flex-end" style={{ paddingTop: 16 }}>
              <Button type="primary" onClick={handleSave} loading={saving}>
                Save Changes
              </Button>
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}
